import React from "react";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";
import { api } from "../lib/api";

const QUICK_PROMPTS = [
  "Hvad koster levering?",
  "Hvordan booker jeg?",
  "Hvor leverer I?",
  "Spor min pakke",
];

export default function ChatWidget() {
  const [open, setOpen] = React.useState(false);
  const [messages, setMessages] = React.useState(() => {
    const cached = sessionStorage.getItem("ema_chat_messages");
    return cached ? JSON.parse(cached) : [];
  });
  const [sessionId, setSessionId] = React.useState(() => sessionStorage.getItem("ema_chat_session") || null);
  const [input, setInput] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const scrollRef = React.useRef(null);

  // Persist messages + session in sessionStorage
  React.useEffect(() => {
    sessionStorage.setItem("ema_chat_messages", JSON.stringify(messages));
  }, [messages]);
  React.useEffect(() => {
    if (sessionId) sessionStorage.setItem("ema_chat_session", sessionId);
  }, [sessionId]);

  // Auto-scroll on new messages
  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // First-open welcome
  React.useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: "assistant",
        text: "Hej 👋 Jeg er EMA Assistant. Spørg løs om priser, booking, levering eller noget helt fjerde — jeg er her 24/7.",
      }]);
    }
  }, [open, messages.length]);

  const send = async (text) => {
    const trimmed = (text || input).trim();
    if (!trimmed || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: trimmed }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat/message", {
        message: trimmed,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      console.error("Chat error:", e);
      setMessages((m) => [...m, {
        role: "assistant",
        text: "Beklager, der opstod en fejl. Skriv venligst til kontakt@emakurer.dk så hjælper vi hurtigt 🛠️",
      }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating launcher button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-launcher"
          className="fixed bottom-20 sm:bottom-24 right-6 z-40 bg-brand-red text-white shadow-2xl hover:scale-105 transition-transform p-4 group"
          aria-label="Åbn chat med EMA Assistant"
        >
          <div className="flex items-center gap-2 pr-1">
            <div className="relative">
              <MessageCircle size={22} />
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-brand-red animate-pulse" />
            </div>
            <span className="hidden sm:inline font-display font-semibold pr-1">Chat med EMA</span>
          </div>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed inset-x-4 bottom-20 sm:inset-x-auto sm:right-6 sm:bottom-24 sm:w-[400px] z-50 bg-white border border-brand shadow-2xl flex flex-col"
          style={{ maxHeight: "min(80vh, 640px)", height: "80vh" }}
        >
          {/* Header */}
          <div className="bg-brand-dark text-white p-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-brand-red flex items-center justify-center flex-shrink-0">
                <Sparkles size={18} />
              </div>
              <div className="min-w-0">
                <div className="font-display font-semibold leading-tight">EMA Assistant</div>
                <div className="text-[11px] uppercase tracking-widest text-white/60 flex items-center gap-1.5 mt-0.5">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  Svarer nu · AI · 24/7
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Luk chat" className="p-2 hover:bg-white/10" data-testid="chat-close">
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-brand-bg2" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div
                key={`${m.role}-${i}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`chat-message-${m.role}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-brand-dark text-white"
                      : "bg-white text-brand-dark border border-brand"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="bg-white border border-brand px-4 py-3 text-sm flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-brand-text2 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-1.5 h-1.5 bg-brand-text2 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-brand-text2 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
          </div>

          {/* Quick prompts */}
          {messages.length <= 1 && (
            <div className="px-4 py-2 flex flex-wrap gap-2 border-t border-brand bg-white flex-shrink-0">
              {QUICK_PROMPTS.map((q) => (
                <button
                  key={q}
                  onClick={() => send(q)}
                  className="text-xs px-3 py-1.5 border border-brand text-brand-dark hover:border-brand-red hover:text-brand-red"
                  data-testid={`chat-quick-${q.slice(0, 10)}`}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-brand p-3 flex gap-2 bg-white flex-shrink-0"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Skriv din besked..."
              className="input-flat flex-1 py-2.5"
              maxLength={500}
              data-testid="chat-input"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              data-testid="chat-send"
              className="bg-brand-red text-white px-4 disabled:opacity-50 hover:bg-brand-dark transition-colors"
              aria-label="Send"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>
          <div className="text-[10px] text-brand-text2 text-center pb-2 px-3 bg-white flex-shrink-0">
            AI-svar kan være upræcise. Ved akut behov: kontakt@emakurer.dk
          </div>
        </div>
      )}
    </>
  );
}
